import { useState } from "react";
import { Mail, Loader2, Check } from "lucide-react";
import { api } from "@/lib/apiClient";
import { log } from "@/lib/log";
import { useLang } from "@/lib/siteI18n";

const GOLD = "#C9A96E";
const inputCls = "w-full bg-transparent border border-white/20 focus:border-[#C9A96E] outline-none px-4 py-3 text-white placeholder-white/30 text-sm transition-colors";

export const NewsletterBlock = ({ source = "journal" }) => {
  const { t } = useLang();
  const tx = t.newsletter;
  const [form, setForm] = useState({ prenom: "", email: "", consent: false });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return setError(tx.errEmail);
    if (!form.consent) return setError(tx.errConsent);
    setError("");
    setStatus("loading");
    try {
      await api.post("/newsletter/subscribe", { email: form.email.trim().toLowerCase(), prenom: form.prenom.trim(), consent: true, source });
      setStatus("done");
    } catch (err) {
      log.error("[newsletter] inscription échouée :", err);
      setError(tx.errSend);
      setStatus("idle");
    }
  };

  return (
    <section className="mt-12 p-8 md:p-10 border rounded-sm" style={{ borderColor: "rgba(201,169,110,0.3)", backgroundColor: "rgba(201,169,110,0.04)" }} data-testid="newsletter-block">
      <div className="flex items-center gap-2 mb-3" style={{ color: GOLD }}>
        <Mail size={16} strokeWidth={1.5} />
        <p className="text-xs uppercase tracking-luxe">{tx.kicker}</p>
      </div>
      <h3 className="font-serif text-2xl md:text-3xl text-white leading-snug mb-3">{tx.title}</h3>
      <p className="text-white/60 leading-relaxed mb-6 text-sm md:text-base">{tx.sub}</p>
      {status === "done" ? (
        <p className="inline-flex items-center gap-2 text-white/85" data-testid="newsletter-success">
          <Check size={18} style={{ color: GOLD }} /> {tx.done}
        </p>
      ) : (
        <form onSubmit={onSubmit} noValidate className="space-y-4" data-testid="newsletter-form">
          <div className="grid md:grid-cols-[1fr_1.4fr_auto] gap-3">
            <input className={inputCls} placeholder={tx.prenom} value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="newsletter-prenom" />
            <input className={inputCls} type="email" placeholder={tx.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="newsletter-email" />
            <button type="submit" disabled={status === "loading"} data-testid="newsletter-submit"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-xs uppercase tracking-[0.15em] font-semibold transition-transform hover:scale-[1.02] disabled:opacity-60"
              style={{ backgroundColor: GOLD, color: "#0A0A12" }}>
              {status === "loading" ? <Loader2 size={14} className="animate-spin" /> : null} {tx.submit}
            </button>
          </div>
          <label className="flex items-start gap-3 text-xs text-white/50 leading-relaxed cursor-pointer">
            <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-0.5 accent-[#C9A96E]" data-testid="newsletter-consent" />
            <span>{tx.consent}</span>
          </label>
          {error && <p className="text-sm text-red-300" data-testid="newsletter-error">{error}</p>}
        </form>
      )}
    </section>
  );
};
